<template>
  <form-base :teleport-target="dialogTeleportTarget">
    <template #form>
      <v-form ref="form" class="pa-4">
        <v-row class="mb-4">
          <v-col>
            <text-field
              data-testid="load-type-name-field"
              :required="true"
              type="text"
              density="compact"
              v-model="loadTypeLocal.name"
              :rules="$validator.rules.required('Load Type Name')"
              :label="t('forms.common.labels.name')"></text-field>
          </v-col>
          <v-col>
            <v-select
              data-testid="load-type-direction-select"
              density="compact"
              v-model="loadTypeLocal.direction"
              :items="loadTypeDirectionOptions"
              required
              :rules="$validator.rules.required('Load Type Direction')"
              :label="t('loadType.forms.loadTypeForm.labels.direction')">
              <template #label>
                {{ t('loadType.forms.loadTypeForm.labels.direction') }}
                <span class="text-red"><strong>*</strong></span>
              </template>
            </v-select>
          </v-col>
        </v-row>
        <v-row class="mb-4">
          <v-col>
            <v-select
              data-testid="load-type-operation-select"
              density="compact"
              v-model="loadTypeLocal.operation"
              required
              :rules="$validator.rules.required('Operation')"
              :items="loadTypeOperationOptions"
              :label="t('loadType.forms.loadTypeForm.labels.operation')">
              <template #label>
                {{ t('loadType.forms.loadTypeForm.labels.operation') }}
                <strong>*</strong>
              </template>
            </v-select>
          </v-col>
          <v-col>
            <v-select
              data-testid="load-type-equipment-select"
              density="compact"
              v-model="loadTypeLocal.equipmentType"
              required
              :rules="$validator.rules.required('Equipment Type')"
              :items="loadTypeEquipmentTypeOptions"
              :label="t('loadType.forms.loadTypeForm.labels.equipmentType')">
              <template #label>
                {{ t('loadType.forms.loadTypeForm.labels.equipmentType') }}
                <strong>*</strong>
              </template>
            </v-select>
          </v-col>
          <v-col>
            <v-select
              density="compact"
              data-testid="load-type-transportation-mode-select"
              v-model="loadTypeLocal.transportationMode"
              required
              :rules="$validator.rules.required('Transportation Mode')"
              :items="loadTypeTransportationModeOptions"
              :label="t('loadType.forms.loadTypeForm.labels.transporationMode')">
              <template #label>
                {{ t('loadType.forms.loadTypeForm.labels.transporationMode') }}
                <strong>*</strong>
              </template>
            </v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <auto-expandable-textarea
              v-model="loadTypeLocal.description"
              :label="t('forms.common.labels.description')"></auto-expandable-textarea>
          </v-col>
        </v-row>
        <load-type-duration v-model="loadTypeLocal.duration_min"></load-type-duration>
        <v-row class="mt-6">
          <v-col>
            <h3 class="mt-6 text-black">
              {{ t('loadType.forms.loadTypeForm.headers.schedulingPortalAvailability') }}
            </h3>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <carrier-scheduling-toggle
              entity="Load Type"
              :disabled="!$rolePermissions.canCreateLoadType"
              v-model="loadTypeLocal.allowCarrierScheduling" />
          </v-col>
        </v-row>
        <template v-if="!omitAdvancedSettings">
          <v-row>
            <v-col>
              <h3 class="mt-6 text-black">
                {{ t('loadType.forms.loadTypeForm.headers.advancedSettings') }}
              </h3>
            </v-col>
          </v-row>
          <v-row class="mt-6">
            <v-col>
              <settings-expansion-panels
                :entity-key="DatabaseEntities.LoadType"
                :entity-parent="warehouse ? DatabaseEntities.Warehouse : DatabaseEntities.Org"
                :should-be-expanded="false"
                @update-settings="updateSettings"
                :entity-settings="loadTypeSettings">
                <template v-slot:append-panel>
                  <v-expansion-panel>
                    <v-expansion-panel-title ripple>
                      {{ t('loadType.forms.loadTypeForm.headers.loadTypeSchedule') }}
                    </v-expansion-panel-title>
                    <v-divider></v-divider>
                    <v-expansion-panel-text>
                      <v-row>
                        <v-col class="mt-6">
                          <time-select-grid
                            :settings-entity="warehouse ?? $org"
                            ref="timeSelect"
                            :toggle-to-edit="false"
                            compact
                            v-model="loadTypeLocal.schedule"
                            :with-copy-from-dock="false"
                            :with-copy-from-warehouse="false"
                            @set-schedule-default="setToDefault(loadTypeLocal)"
                            @set-schedule-closed="clearSchedule(loadTypeLocal)"></time-select-grid>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </template>
              </settings-expansion-panels>
            </v-col>
          </v-row>
        </template>
        <v-row class="my-4" v-else>
          <v-col>
            <v-icon size="18" color="gray darken-3" class="mr-1 mb-1">mdi-information</v-icon>
            <span class="text-grey-darken-3 font-size-medium">
              {{
                t(
                  'loadType.forms.loadTypeForm.messages.otherAdvancedSettingsCanBeFoundOnTheLoadTypesPage'
                )
              }}.
            </span>
          </v-col>
        </v-row>
      </v-form>
    </template>

    <template #form-actions>
      <action-group
        @cancel="$emit('close')"
        :confirm-icon="null"
        class="mb-2"
        :cancel-icon="null"
        :confirm-label="t('loadType.forms.loadTypeForm.buttons.saveLoadType')"
        @confirm="promptForSubmission"></action-group>
    </template>
  </form-base>
</template>

<script>
import {
  DatabaseEntities,
  LoadTypeDirection,
  LoadTypeEquipmentType,
  LoadTypeOperation,
  LoadTypeTransportationMode,
  MAX_LOAD_TYPES_PER_WAREHOUSE,
  t
} from '@nova/core';
import customMessageMixin from '@satellite/components/mixins/customMessageMixin';
import scheduleMixin from '@satellite/components/mixins/scheduleMixin';
import { camelCase } from 'lodash';
import { inject } from 'vue';

export default {
  mixins: [scheduleMixin],
  props: {
    warehouse: {
      type: Object,
      required: false
    },
    loadType: {
      type: Object,
      required: false
    },
    omitAdvancedSettings: {
      type: Boolean,
      default: false
    },
    skipDirtyValidation: {
      type: Boolean,
      default: false
    },
    compact: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      DatabaseEntities,
      loadTypeLocal: {},
      formInitialized: false,
      interval: 30
    };
  },
  computed: {
    loadTypeDirectionOptions() {
      return this.buildOptions(LoadTypeDirection, 'direction');
    },
    loadTypeOperationOptions() {
      return this.buildOptions(LoadTypeOperation, 'operation');
    },
    loadTypeEquipmentTypeOptions() {
      return this.buildOptions(LoadTypeEquipmentType, 'equipmentType');
    },
    loadTypeTransportationModeOptions() {
      return this.buildOptions(LoadTypeTransportationMode, 'transportationMode');
    },
    loadTypeSettings() {
      return this.loadTypeLocal.settings ?? {};
    },
    maxLoadtypeWarning() {
      const maxLoadTypes = MAX_LOAD_TYPES_PER_WAREHOUSE;
      const isWarehouseLoadType = Boolean(this.warehouse);
      return customMessageMixin.computed.maxLoadTypeLimitWarning(maxLoadTypes, isWarehouseLoadType);
    },
    durationConfirmationChangeBody() {
      return `${this.t(
        'loadType.forms.loadTypeForm.messages.areYouSureYouWantToChangeThisLoadTypes'
      )} <strong><em>${this.t('global.basicTerms.duration').toLowerCase()}</em></strong>? ${this.t(
        'loadType.forms.loadTypeForm.messages.doingThisWillNotChangeAnyExistingAppointmentsDurationThisWillNowBeTheDefaultDurationForAnyNewlyCreatedAppointmentAfterThisPoint'
      )}.
              <br/><br/>
              <strong>${this.t('global.basicTerms.notes')}:</strong> ${this.t(
                      'loadType.forms.loadTypeForm.messages.thisWillImpactAllWarehousesUsingThisLoadType'
                    )}.`;
    },
    submitAction() {
      return this.loadType?.id ? 'LoadTypes/updateLoadType' : 'LoadTypes/createLoadType';
    }
  },
  methods: {
    t,
    buildOptions(enumObject, group) {
      return Object.entries(enumObject).map(([key, value]) => ({
        value,
        title: this.t(`loadType.forms.loadTypeForm.options.${group}.${camelCase(key)}`)
      }));
    },
    updateSettings(settings) {
      this.loadTypeLocal.settings = settings;
    },
    getChangedFields() {
      let fields = [];

      const isDifferent = (a, b) =>
        typeof a === 'object' || typeof b === 'object'
          ? JSON.stringify(a) !== JSON.stringify(b)
          : a !== b;

      if (this.loadType) {
        Object.keys(this.loadType).forEach(key => {
          if (isDifferent(this.loadType[key], this.loadTypeLocal[key])) {
            fields.push(key);
          }
        });
      } else {
        Object.keys(this.loadTypeLocal).forEach(key => {
          if (typeof this.loadTypeLocal[key] !== 'undefined' && this.loadTypeLocal[key] !== null) {
            fields.push(key);
          }
        });
      }

      return fields;
    },
    submit() {
      this.$refs.timeSelect?.saveChanges();
      this.$store
        .dispatch(this.submitAction, this.loadTypeLocal)
        .then(response => {
          if (response?.data) {
            this.$emit('saved', response.data);

            this.setDirtyStatus(false);

            this.$emit('close');
          }
        })
        .catch(e => {
          if (e.status === 500) {
            this.notify(`Error when saving loadtype`, 'error');
          }
        });
    },
    async promptForSubmission() {
      if (await this.$isFormValid(this.$refs.form)) {
        if (this.loadType?.id && this.loadTypeLocal.duration_min !== this.loadType?.duration_min) {
          this.$confirm(this.durationConfirmationChangeBody, {
            color: 'warning'
          }).then(confirmed => {
            if (confirmed) {
              this.submit();
            }
          });
        } else {
          this.submit();
        }
      }
    }
  },
  watch: {
    loadTypeLocal: {
      handler() {
        if (!this.skipDirtyValidation) {
          this.setDirtyStatus(this.formInitialized);
        }
      },
      deep: true
    }
  },
  beforeMount() {
    this.loadTypeLocal = this.loadType
      ? { ...this.loadType }
      : {
          name: '',
          description: '',
          direction: '',
          duration_min: 15,
          allowCarrierScheduling: true,
          warehouseId: this.warehouse?.id,
          schedule: null
        };
  },
  mounted() {
    setTimeout(() => {
      this.formInitialized = true;
    }, 1000);
  },
  setup(props) {
    const dialogTeleportTarget = props.compact ? inject('dialogActionsRef') : null;
    return { dialogTeleportTarget };
  }
};
</script>
